// Einstellungen → System → Zurücksetzen (W21, W22 of APP-PARITY.md "Detail-Abgleich", FR-24): WLAN reset and factory reset, owner only
// (members do not see the card), each with the explanation of what follows as its safety question; the API asks for a
// fresh login. After a factory reset the machine reports the end of its pairing and leaves the app (onEnded). Board
// "System": "Zurücksetzen (i)", the two as red rows with a line between, at the left like the card's other rows.
import { useEffect, useId, useState } from 'react';
import { resetActions, type ActionView } from '../../core/index.js';
import { Banner, Card, CardHeader } from '../components/index.js';
import { useMachine } from '../machine-context.js';
import { AskSheet } from '../tabs/parts.js';

export function ResetCard({ onEnded }: { onEnded?: () => void }) {
  const { ctx, send } = useMachine();
  const r = resetActions(ctx);
  const [factorySent, setFactorySent] = useState(false);
  const ended = ctx.view.accessEnded;
  // the factory reset ended the pairing: back to the machine list (the machine pairs anew at the machine)
  useEffect(() => {
    if (factorySent && ended) onEnded?.();
  }, [factorySent, ended, onEnded]);
  if (!r) return null;
  return (
    <Card className="tab-card reset-card" data-testid="reset-card">
      <CardHeader
        title={ctx.tr('sReset')}
        info={{ text: ctx.tr('resetOwnerInfo'), label: ctx.tr('infoShow'), testId: 'reset-info' }}
      />
      {factorySent && <Banner tone="info">{ctx.tr('factoryPending')}</Banner>}
      <ResetButton action={r.wifi} testId="wifi-reset" />
      <ResetButton
        action={r.factory}
        testId="factory-reset"
        onDone={() => setFactorySent(true)}
        send={send}
      />
    </Card>
  );
}

function ResetButton({
  action,
  testId,
  onDone,
  send,
}: {
  action: ActionView;
  testId: string;
  onDone?: () => void;
  send?: ReturnType<typeof useMachine>['send'];
}) {
  const m = useMachine();
  const [asking, setAsking] = useState(false);
  const titleId = useId();
  const run = async () => {
    setAsking(false);
    const o = await (send ?? m.send)(action.input);
    if (o.result === 'done') onDone?.();
  };
  return (
    <>
      <button
        type="button"
        className="row-danger"
        aria-busy={action.busy || undefined}
        disabled={!action.enabled}
        onClick={() => setAsking(true)}
        data-testid={testId}
      >
        {action.label}
        {action.busy && <span className="spinner spinner-sm" aria-hidden="true" />}
      </button>
      {asking && action.ask && (
        <AskSheet
          ask={action.ask}
          titleId={titleId}
          onYes={() => void run()}
          onNo={() => setAsking(false)}
          testId={testId}
        />
      )}
    </>
  );
}
